import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, Select, TextArea, type SelectOption, SectionCard } from "@/deps/ui-library";
import { api } from "@/api";
import { useAccounts, useBillingItems, useSaveCreditNoteDraft } from "@/api/queries";
import type { CreditNote, CreditNoteReason, InvoiceSummary, Party } from "@/api/types";
import { Dialog } from "@/components/Dialog";
import { LineEditor, type EditableLine } from "@/components/LineEditor/LineEditor";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { TotalsBlock, VatBreakdownTable } from "@/components/TotalsBlock";
import { calculateTotals } from "@/lib/invoiceTotals";
import { sellingAccounts } from "@/lib/sellingAccounts";
import { formatAmount } from "@/lib/money";
import { useTaxWords } from "@/app/session";

const REASONS: CreditNoteReason[] = [
  "Billing error",
  "Goods returned",
  "Post-invoice discount",
  "Price adjustment",
];

/** The value an RDS single `Select` hands back, as the plain string this screen holds. */
function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

/**
 * Issue a credit note against an invoice.
 *
 * A credit note is how an issued invoice is corrected — issued documents are never edited
 * (ADR-0023). The screen enforces the two things that would otherwise be discovered at Save: it
 * must say what it credits, and it cannot credit more than the invoice was for. An invoice already
 * paid can be credited: the customer then holds what is over in credit.
 *
 * Given a draft and its customer, it edits that draft.
 */
export function NewCreditNotePage({
  editing,
}: { editing?: { note: CreditNote; customer: Party; invoiceId: string } } = {}) {
  const say = useTaxWords();
  const navigate = useNavigate();

  // Revenue accounts from the chart. This screen carried `acc_4010`/`acc_4020` of its own, which
  // were in no chart of accounts — so a credit note it issued named accounts that did not exist.
  const { data: chart } = useAccounts();
  const revenueAccounts = sellingAccounts(chart);
  const { data: billingItems } = useBillingItems({});
  const save = useSaveCreditNoteDraft();

  const draft = editing?.note;
  const back = draft
    ? ({ to: "/sales/credit-notes/$id", params: { id: draft.id } } as const)
    : ({ to: "/sales/credit-notes" } as const);

  const [customer, setCustomer] = useState<Party | null>(editing?.customer ?? null);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [against, setAgainst] = useState<string>(editing?.invoiceId ?? "");
  const [reason, setReason] = useState<CreditNoteReason>(draft?.reason ?? "Billing error");
  const [detail, setDetail] = useState(draft?.detail ?? "");
  const [confirming, setConfirming] = useState(false);
  const [lines, setLines] = useState<EditableLine[]>(
    draft
      ? draft.lines.map((line) => ({
          id: String(line.n),
          description: line.itemName,
          accountId: line.accountId,
          category: line.vatCategory,
          rate: line.vatRatePct,
          grossPrice: line.unitPrice,
          quantity: line.quantity,
          itemCode: line.itemCode,
          exemptionReason: line.exemptionReason,
          billingItemId: line.billingItemId,
        }))
      : [
          {
            id: "1",
            description: "",
            accountId: "",
            category: "S",
            rate: "13",
            grossPrice: "",
            quantity: "1",
          },
        ],
  );

  // Issued invoices, whether or not anything is still owed on them.
  const customerId = customer?.id;
  useEffect(() => {
    if (customerId === undefined) {
      setInvoices([]);
      return;
    }
    let live = true;
    void api.invoices.list({ customerId, status: "approved", pageSize: 100 }).then((page) => {
      if (live) setInvoices(page.items.filter((i) => i.customerId === customerId && i.number));
    });
    return () => {
      live = false;
    };
  }, [customerId]);

  const totals = calculateTotals(lines);
  const invoice = invoices.find((i) => i.id === against);

  const exceedsInvoice =
    invoice !== undefined && Number(totals.totalWithVat) > Number(invoice.total);
  const inCredit =
    invoice !== undefined && !exceedsInvoice
      ? Math.max(0, Number(totals.totalWithVat) - Number(invoice.balance))
      : 0;

  /** See `NewBillPage`: the chart arrives after the first render, so fill the gap during it. */
  const withAccounts = lines.map((line) =>
    line.accountId === "" ? { ...line, accountId: revenueAccounts[0]?.id ?? "" } : line,
  );

  const canIssue =
    customer !== null &&
    against !== "" &&
    Number(totals.totalWithVat) > 0 &&
    !exceedsInvoice &&
    withAccounts.every((line) => line.accountId !== "") &&
    !save.isPending;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={draft ? "Edit credit note" : "New credit note"}
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => void navigate(back)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!canIssue}
              onClick={() => setConfirming(true)}
            >
              Review and save
            </Button>
          </span>
        }
      />

      <div className="flex flex-col gap-4">
        <SectionCard title="Credit note">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <PartyCombobox
              label="Customer"
              required
              value={customer}
              onChange={(party) => {
                setCustomer(party);
                setAgainst("");
              }}
              search={(q) => api.parties.search("customer", q)}
            />

            <Select
              label="Credits invoice"
              required
              placeholder="Choose an invoice…"
              errorText={
                exceedsInvoice ? `That invoice was for ${formatAmount(invoice!.total)}.` : undefined
              }
              options={invoices.map((option) => ({
                value: option.id,
                label: `${option.number} · ${formatAmount(option.total)} · ${formatAmount(option.balance)} outstanding`,
              }))}
              value={against}
              onChange={(details) => setAgainst(chosen(details))}
              disabled={customer === null}
            />

            <Select
              label="Reason"
              required
              options={REASONS.map((option) => ({ value: option, label: option }))}
              value={reason}
              onChange={(details) => {
                const picked = chosen(details);
                if (picked) setReason(picked as CreditNoteReason);
              }}
              showClearButton={false}
            />

            <TextArea
              label="Note"
              rows={3}
              value={detail}
              onChange={(event) => setDetail(event.target.value)}
            />
          </div>
        </SectionCard>

        <div className="flex flex-col gap-4">
          <SectionCard title="What is being credited">
            <LineEditor
              lines={withAccounts}
              onChange={setLines}
              accounts={revenueAccounts}
              items={billingItems}
            />
          </SectionCard>

          <div className="grid gap-4 md:grid-cols-2">
            <SectionCard title={say("VAT breakdown")}>
              <VatBreakdownTable totals={totals} />
            </SectionCard>
            <SectionCard title="Totals">
              <TotalsBlock totals={totals} />
            </SectionCard>
          </div>
        </div>
      </div>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Save this credit note?"
        description="It is saved as a draft, with no number. Somebody else issues it, because whoever enters a document may not issue it."
        footer={
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={save.isPending}
            onClick={() => {
              // The dialog stays open until the server has answered. Closing it first — which is
              // what this did — leaves a refusal with nowhere to be shown, and the rejection was
              // not caught at all.
              save.mutate(
                {
                  id: draft?.id,
                  customerId: customer!.id,
                  precedingInvoiceId: against,
                  reason,
                  detail: detail || undefined,
                  lines: withAccounts.map((line) => ({
                    itemName: line.description,
                    quantity: line.quantity,
                    unitPrice: line.grossPrice,
                    vatCategory: line.category,
                    ratePct: line.rate ?? "0",
                    accountId: line.accountId,
                    itemCode: line.itemCode,
                    exemptionReason: line.exemptionReason,
                    billingItemId: line.billingItemId,
                  })),
                },
                {
                  onSuccess: (note) => {
                    setConfirming(false);
                    void navigate({ to: "/sales/credit-notes/$id", params: { id: note.id } });
                  },
                },
              );
            }}
          >
            {save.isPending ? "Saving" : "Save draft"}
          </Button>
        }
      >
        {save.isError ? (
          <p role="alert" className="text-bad-text mb-2 text-sm">
            {save.error instanceof Error
              ? save.error.message
              : "That credit note could not be saved."}
          </p>
        ) : null}

        <p className="text-fg text-sm">
          Crediting <span className="num font-medium">{formatAmount(totals.totalWithVat)}</span> to{" "}
          {customer?.name}.
        </p>
        {inCredit > 0 ? (
          <p className="text-fg-muted mt-2 text-sm">
            {invoice?.number} has {formatAmount(invoice!.balance)} outstanding, so{" "}
            <span className="num">{formatAmount(inCredit.toFixed(2))}</span> stays in credit on the
            customer's account.
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}
